
import { createElement } from "react"

import UiCard from "@mui/material/Card"
import UiListItem from "@mui/material/ListItem"
import { styled } from "@mui/material/styles"
import Typography from "@mui/material/Typography"

import { DateRangeIcon } from "@l2r-front/l2r-icons"

import { styledForMobile } from "../../utils/styledForMobile"

import { Chip as UiChip } from "../Chip"

const ITEM_HEIGHT = 130
const CHIP_ICON_SIZE = 24
const INFO_ICON_SIZE = 14

export const ListItem = styled(UiListItem)(({ theme }) => ({
    padding: 0,
    marginBottom: theme.spacing(2),
    height: ITEM_HEIGHT,
}))

export const Card = styledForMobile(UiCard, ["greydOut"])(({ theme, isMobile, greydOut }) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(isMobile ? 2 : 3),
    justifyContent: "space-between",
    alignItems: "center",
    padding: 0,
    paddingRight: theme.spacing(2),
    backgroundColor: greydOut ? theme.palette["action/disabledBackground"].main : theme.palette["common/white_states"].main,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,

    ":hover": {
        boxShadow: theme.customShadow("LCS/Shadow-simple"),
        cursor: "pointer",
    },
}))

export const Thumbnail = styled("img")(() => ({
    width: "auto",
    height: "auto",
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
}))

export const Content = styled("div")(({ theme }) => ({
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    padding: theme.spacing(4, 0),
    minWidth: 0,
}))

export const FileContent = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1),
}))

export const FileName = styled(Typography)(() => ({
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "100%",
}))

export const FileInfoContainer = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
    width: "100%",
}))

export const FileInfo = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),

    "&:last-child": {
        flexShrink: 1,
        minWidth: 0,
    },
}))

export const FileInfoText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
}))

export const PointSeparator = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontFamily: "Poppins",
    fontSize: 14,
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "100%",
}))

const infoIconStyle = ({ theme }) => ({
    height: INFO_ICON_SIZE,
    width: INFO_ICON_SIZE,
    color: theme.palette["action/active"].main,
})

export const DateIcon = styled(DateRangeIcon)(infoIconStyle)

const SecondaryIconBase = ({ icon: Icon, ...rest }) => Icon ? createElement(Icon, rest) : null

export const SecondaryIcon = styled(SecondaryIconBase)(infoIconStyle)

export const ChipsContainer = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    alignContent: "flex-start",
    gap: theme.spacing(1),
    flexWrap: "nowrap",
}))

type StyledChipProps = {
    iconOnly?: boolean
}
export const Chip = styled(UiChip, { shouldForwardProp: (prop) => prop !== "iconOnly" })<StyledChipProps>(({ theme, iconOnly = false }) => ({
    minWidth: iconOnly && "32px",
    color: theme.palette["text/secondary"].main,
    border: `1px solid ${theme.palette.divider}`,

    "& > .MuiChip-icon": {
        height: 16,
        width: 16,
        margin: iconOnly ? "auto" : `0 0 0 ${theme.spacing(2)}`,
        color: theme.palette["action/active"].main,
    },

    "& > .MuiChip-label": {
        display: iconOnly && "none",
        fontSize: 12,
        paddingRight: !iconOnly && 11,
    },

    "&:last-child": {
        flexShrink: 1,
        minWidth: 0,
    },
}))

export const TypeIcon = styled("img")(() => ({
    height: CHIP_ICON_SIZE,
    objectFit: "contain",
}))